import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../lib/firebaseService';
import { Shield, User, Loader2, AlertCircle, Trash2 } from 'lucide-react';
import { t } from '../utils/translation';
import '../styles/components-extended.css';
import { useAuth } from '../context/AuthContext';

export default function Settings() {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm] = useState('');

    const { data: users, isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: userService.getUsers
    });

    const roleMutation = useMutation({
        mutationFn: ({ id, role }) => userService.updateUserRole(id, role),
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: userService.deleteUser,
        onSuccess: () => {
            queryClient.invalidateQueries(['users']);
        }
    });

    const handleRoleToggle = (user) => {
        // Prevent changing own role for safety
        if (user.email === currentUser?.email) return;

        const newRole = user.role === 'admin' ? 'staff' : 'admin';
        roleMutation.mutate({ id: user.id, role: newRole });
    };

    const handleDelete = async (user) => {
        if (user.email === currentUser?.email) return;
        if (window.confirm(`${t('SETTINGS.DELETE_CONFIRM')} ${user.email}?`)) {
            await deleteMutation.mutateAsync(user.id);
        }
    };

    if (isLoading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-purple-600" size={32} /></div>;

    // Filter users (if we had many)
    const filteredUsers = users?.filter(u => u.email.toLowerCase().includes(searchTerm.toLowerCase())) || [];

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('SETTINGS.TITLE')}</h1>
                    <p className="page-subtitle">{t('SETTINGS.SUBTITLE')}</p>
                </div>
            </div>

            <div className="content-card">
                <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">{t('SETTINGS.TEAM_MEMBERS')}</h3>
                    <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                        {filteredUsers.length} {t('SETTINGS.ACTIVE_USERS')}
                    </div>
                </div>

                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('SETTINGS.TABLE.USER')}</th>
                                <th className="hidden-mobile">{t('SETTINGS.TABLE.EMAIL')}</th>
                                <th>{t('SETTINGS.TABLE.ROLE')}</th>
                                <th className="text-right">{t('SETTINGS.TABLE.ACTION')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length > 0 ? filteredUsers.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold mr-3">
                                                {user.email.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="font-medium text-gray-900 truncate block max-w-[100px] md:max-w-none" title={user.displayName}>
                                                {user.displayName || 'User'}
                                            </span>
                                            {user.email === currentUser?.email && (
                                                <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0">{t('SETTINGS.YOU')}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-gray-600 font-medium hidden-mobile">
                                        <div className="truncate max-w-[150px]" title={user.email}>{user.email}</div>
                                    </td>
                                    <td>
                                        {/* Improved Role Badge Logic */}
                                        {user.role === 'super_admin' ? (
                                            <span className="badge badge-purple bg-purple-100 text-purple-800 border-purple-200">
                                                <Shield size={12} className="mr-1 fill-current" /> {t('SETTINGS.ROLES.SUPER_ADMIN')}
                                            </span>
                                        ) : user.role === 'admin' ? (
                                            <span className="badge badge-blue bg-blue-50 text-blue-700 border-blue-200">
                                                <Shield size={12} className="mr-1" /> {t('SETTINGS.ROLES.ADMIN')}
                                            </span>
                                        ) : (
                                            <span className="badge badge-gray">
                                                <User size={12} className="mr-1" /> {t('SETTINGS.ROLES.STAFF')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        {/* Action Button Logic */}
                                        {user.role !== 'super_admin' && user.email !== currentUser?.email ? (
                                            <div className="flex justify-end items-center gap-2">
                                                <button
                                                    onClick={() => handleRoleToggle(user)}
                                                    disabled={roleMutation.isPending}
                                                    className={`
                                                        btn h-8 px-3 text-xs
                                                        ${user.role === 'admin'
                                                            ? 'btn-secondary text-red-600 hover:bg-red-50 hover:border-red-200'
                                                            : 'btn-primary'}
                                                    `}
                                                >
                                                    {roleMutation.isPending ? (
                                                        <Loader2 className="animate-spin" size={14} />
                                                    ) : (
                                                        user.role === 'admin' ? t('SETTINGS.DEMOTE') : t('SETTINGS.PROMOTE')
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                    title={t('UI.DELETE_USER')}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400 italic">
                                                {user.role === 'super_admin' ? t('SETTINGS.IMMUTABLE') : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4} className="text-center py-10 text-gray-500">
                                        {t('SETTINGS.NO_USERS')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Info Box */}
                <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
                    <AlertCircle className="text-blue-600 shrink-0 mt-0.5" size={20} />
                    <div>
                        <h4 className="font-semibold text-blue-900 text-sm">{t('SETTINGS.PERMISSIONS_GUIDE.TITLE')}</h4>
                        <ul className="mt-1 text-sm text-blue-800 space-y-1">
                            <li>• <strong>{t('SETTINGS.ROLES.ADMIN')}</strong> {t('SETTINGS.PERMISSIONS_GUIDE.ADMIN')}</li>
                            <li>• <strong>{t('SETTINGS.ROLES.STAFF')}</strong> {t('SETTINGS.PERMISSIONS_GUIDE.STAFF')}</li>
                        </ul>
                    </div>
                </div>

                {/* Maintenance Zone */}
                <div className="mt-8 mb-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Maintenance</h3>
                    <div className="p-6 bg-yellow-50 rounded-xl border border-yellow-200 flex items-center justify-between">
                        <div>
                            <h4 className="font-semibold text-yellow-900">Réparer les accents</h4>
                            <p className="text-sm text-yellow-800 mt-1 max-w-lg">
                                Corrige les caractères mal encodés (ex: "BiÃ¨re" -> "Bière") dans les produits et les ventes importées.
                            </p>
                        </div>
                        <FixEncodingButton />
                    </div>
                </div>

                {/* Danger Zone */}
                {currentUser?.email && (
                    <div className="mt-12 pt-8 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-red-600 mb-4">{t('SETTINGS.DANGER_ZONE.TITLE')}</h3>
                        <div className="p-6 bg-red-50 rounded-xl border border-red-100 flex items-center justify-between">
                            <div>
                                <h4 className="font-semibold text-red-900">{t('SETTINGS.DANGER_ZONE.RESET_TITLE')}</h4>
                                <p className="text-sm text-red-700 mt-1 max-w-lg">
                                    {t('SETTINGS.DANGER_ZONE.RESET_DESC')}
                                </p>
                            </div>
                            <ResetDbButton />
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
}

function FixEncodingButton() {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    const handleFix = async () => {
        setIsLoading(true);
        try {
            const { productService } = await import('../lib/firebaseService');
            const count = await productService.fixEncodings();
            await queryClient.invalidateQueries();
            alert(`Succès ! ${count} éléments corrigés.`);
            window.location.reload();
        } catch (error) {
            console.error("Fix failed", error);
            alert("Erreur: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            onClick={handleFix}
            disabled={isLoading}
            className="btn bg-white text-yellow-700 hover:bg-yellow-50 border-yellow-300 shadow-sm"
        >
            {isLoading ? <Loader2 className="animate-spin mr-2" /> : <Shield className="mr-2" size={18} />}
            {isLoading ? 'Correction...' : 'Réparer maintenant'}
        </button>
    );
}

function ResetDbButton() {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    const handleReset = async () => {
        if (
            window.confirm(t('SETTINGS.DANGER_ZONE.RESET_CONFIRM_1')) &&
            window.confirm(t('SETTINGS.DANGER_ZONE.RESET_CONFIRM_2'))
        ) {
            setIsLoading(true);
            try {
                // Dynamic import to avoid circular dependencies if any
                const { resetService } = await import('../lib/firebaseService');
                await resetService.clearDatabase();

                // Invalidate all queries
                await queryClient.invalidateQueries();

                alert(t('SETTINGS.DANGER_ZONE.SUCCESS'));
                window.location.reload(); // Hard reload to clear any local state/caches
            } catch (error) {
                console.error("Reset failed", error);
                alert(t('SETTINGS.DANGER_ZONE.FAILED') + error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <button
            onClick={handleReset}
            disabled={isLoading}
            className="btn bg-red-600 text-white hover:bg-red-700 border-red-600 shadow-sm"
        >
            {isLoading ? <Loader2 className="animate-spin mr-2" /> : <Trash2 className="mr-2" size={18} />}
            {isLoading ? t('SETTINGS.DANGER_ZONE.CLEARING') : t('SETTINGS.DANGER_ZONE.RESET_BTN')}
        </button>
    );
}
